import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { UsdCents, UsdDollars, zUsdCents, zUsdDollars } from "../currency";
import { AeoCurrentUsageData } from "./aeo/aeo-billing-types";
import { CheckoutOptimizationCurrentUsageData } from "./checkout-optimization/checkout-optimization-billing-types";
import { ImsCurrentUsageData } from "./ims/ims-billing-types";
import { ImsForecastingCurrentUsageData } from "./ims/ims-forecasting-billing-types";
import { MarketingEmailCurrentUsageData } from "./marketing/email-billing-types";
import { MarketingSmsCurrentUsageData } from "./marketing/sms-billing-types";
import { RecoverCurrentUsageData } from "./recover/recover-billing-types";
import { ReturnsAiExchangeCurrentUsageData } from "./returns/ai-exchange/ai-exchange-billing-types";
import { ReturnsCurrentUsageData } from "./returns/returns-billing-types";
import {
  BillingPeriod,
  LineItem,
  RecurringInvoiceData,
  ServiceInvoiceType,
  SupportedBillingEngineInvoiceType,
} from "./service-invoice";
import { OrderTrackingCurrentUsageData } from "./tracking/tracking-billing-types";

/**
 * Core types for the unified billing system
 */

export enum PaymentProviderType {
  SHOPIFY = "shopify",
  STRIPE = "stripe",
}

export interface CreateInvoiceParams<TInvoice, TSettings> {
  teamId: string;
  lineItems: LineItem[];
  recurringInvoiceData?: RecurringInvoiceData;
  lastInvoice: TInvoice | null;
  settings: TSettings;
  breakdowns: any;
  usageBillingWindowConfigId?: string;
  billingDueDate: Date;
}

/**
 * Configuration for subscription billing returned by providers
 */
export interface SubscriptionConfig {
  planName: string; // e.g., "Email plan", "SMS credits plan"
  periodFeeInCents: UsdCents;
  renewOnPeriodEnd: boolean;
  billingPeriod: BillingPeriod;
  quantity?: number; // free emails/credits per period
}

/**
 * Parameters for creating subscription line items
 */
export interface CreateSubscriptionLineItemsParams<
  TSettings extends BaseBillingSettings,
> {
  subscriptionConfig: SubscriptionConfig;
  settings: TSettings; // Provider-specific settings
  startDate: Date;
  endDate: Date;
}

/**
 * Information about billing periods calculated by the engine
 */
export interface BillingPeriodInfo {
  nextInvoiceStart: Date;
  nextInvoiceEnd: Date;
  nextPeriodStart: Date;
  nextPeriodEnd: Date;
}

/**
 * Core billing data returned by providers for current billing summary
 */
export interface CurrentBillingData<TUsageData> {
  subscriptionPrice: UsdDollars;
  totalInvoicePrice: UsdDollars;
  usage: TUsageData;
}

/**
 * Structured billing summary that replaces raw invoice calculation
 */
export interface CurrentBillingSummary<TUsageData> {
  // Common fields for all products
  subscriptionPrice: UsdDollars;
  totalInvoicePrice: UsdDollars;
  billingDueDate: Date;
  period: BillingPeriod;

  // Provider-specific usage data
  usage: TUsageData;
}

export type InvoiceTypeToUsageType<
  T extends SupportedBillingEngineInvoiceType,
> = {
  [ServiceInvoiceType.MARKETING_EMAIL]: MarketingEmailCurrentUsageData;
  [ServiceInvoiceType.MARKETING_SMS]: MarketingSmsCurrentUsageData;
  [ServiceInvoiceType.ORDER_TRACKING]: OrderTrackingCurrentUsageData;
  [ServiceInvoiceType.RETURN_AI_EXCHANGE]: ReturnsAiExchangeCurrentUsageData;
  [ServiceInvoiceType.RETURNS]: ReturnsCurrentUsageData;
  [ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION]: CheckoutOptimizationCurrentUsageData;
  [ServiceInvoiceType.AEO]: AeoCurrentUsageData;
  [ServiceInvoiceType.IMS]: ImsCurrentUsageData;
  [ServiceInvoiceType.IMS_FORECASTING]: ImsForecastingCurrentUsageData;
  [ServiceInvoiceType.RECOVER]: RecoverCurrentUsageData;
}[T];

/**
 * Base billing settings interface with common fields across all billing products.
 * This replaces the need for providers to work directly with Mongoose Hydrated objects.
 */
export type BaseBillingSettings = z.infer<typeof baseBillingSettingsSchema>;
export const baseBillingSettingsSchema = z.object({
  enabled: z.boolean(),
  startDate: z.date().nullish(),
  period: createEnum(BillingPeriod, { enumName: "BillingPeriod" }),
  periodFeeInCents: zUsdCents,
  renewOnPeriodEnd: z.boolean(),
  targetBillingDay: z.number().min(1).max(28).optional(),
});

export type BillingEngineStripeMetadata = z.infer<
  typeof billingEngineStripeMetadataSchema
>;
export const billingEngineStripeMetadataSchema = z.object({
  type: z.literal("billing_engine"),
  invoiceId: z.string(),
  teamId: z.string(),
  invoiceType: createEnum(ServiceInvoiceType, {
    enumName: "ServiceInvoiceType",
  }),
  retryCount: z.number().optional(),
  chargeAmountInDollars: z
    .union([
      z.string().transform((val) => parseFloat(val) as UsdDollars),
      zUsdDollars,
    ])
    .optional(), // TODO: Remove optional in November 2025
});
